// Meeting storage and synchronization system
// Handles meeting data persistence and calendar integration

import ProfileEnhancementStorage from './profileEnhancementStorage.js';
import { logger } from '../utils/logger';

const MEETINGS_KEY = 'meetingsync_meetings';
const MEETING_REQUESTS_KEY = 'meetingsync_meeting_requests';

export const MeetingStorage = {
  // Get all meetings
  getAllMeetings() {
    try {
      const meetings = localStorage.getItem(MEETINGS_KEY);
      return meetings ? JSON.parse(meetings) : [];
    } catch (error) {
      logger.error('Error getting meetings', { error: error.message });
      return [];
    }
  },

  // Get meetings for a specific user
  getMeetingsForUser(userId, userType) {
    const meetings = this.getAllMeetings();
    if (userType === 'sales') {
      return meetings.filter(m => m.salesRepId === userId.toString() || m.salesRepId === userId);
    } else if (userType === 'customer') {
      return meetings.filter(m => {
        // Check if user is primary customer
        if (m.customerId === userId.toString() || m.customerId === userId) {
          return true;
        }
        // Check if user is in physicians array
        if (m.physicians && Array.isArray(m.physicians)) {
          return m.physicians.some(physician => 
            physician.id === userId.toString() || physician.id === userId
          );
        }
        return false;
      });
    }
    return [];
  },

  // Save a new meeting
  saveMeeting(meetingData) {
    try {
      const meetings = this.getAllMeetings();
      
      const newMeeting = {
        ...meetingData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: meetingData.status || 'pending',
        sync: {
          needsSync: true,
          lastSynced: null
        }
      };
      
      meetings.push(newMeeting);
      localStorage.setItem(MEETINGS_KEY, JSON.stringify(meetings));
      
      // Generate calendar event
      this.generateCalendarEvent(newMeeting);
      
      return newMeeting;
    } catch (error) {
      logger.error('Error saving meeting', { error: error.message });
      return null;
    }
  },

  // Update an existing meeting
  updateMeeting(meetingId, updates) {
    try {
      const meetings = this.getAllMeetings();
      const meetingIndex = meetings.findIndex(m => m.id === meetingId);
      
      if (meetingIndex >= 0) {
        meetings[meetingIndex] = {
          ...meetings[meetingIndex],
          ...updates,
          updatedAt: new Date().toISOString(),
          sync: {
            needsSync: true,
            lastSynced: null
          }
        };
        
        localStorage.setItem(MEETINGS_KEY, JSON.stringify(meetings));
        
        // Update calendar event
        this.generateCalendarEvent(meetings[meetingIndex]);
        
        return meetings[meetingIndex];
      }
      return null;
    } catch (error) {
      logger.error('Error updating meeting', { error: error.message });
      return null;
    }
  },

  // Delete a meeting
  deleteMeeting(meetingId) {
    try {
      const meetings = this.getAllMeetings();
      const filteredMeetings = meetings.filter(m => m.id !== meetingId);
      localStorage.setItem(MEETINGS_KEY, JSON.stringify(filteredMeetings));
      return true;
    } catch (error) {
      logger.error('Error deleting meeting', { error: error.message });
      return false;
    }
  },

  // Get meeting by ID
  getMeetingById(meetingId) {
    const meetings = this.getAllMeetings();
    return meetings.find(m => m.id === meetingId);
  },

  // Get upcoming meetings for a user
  getUpcomingMeetings(userId, userType, limit = 5) {
    const meetings = this.getMeetingsForUser(userId, userType);
    const now = new Date();
    
    return meetings
      .filter(m => {
        const meetingDate = new Date(`${m.date} ${m.time}`);
        return meetingDate >= now && (m.status === 'confirmed' || m.status === 'pending');
      })
      .sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.time}`);
        const dateB = new Date(`${b.date} ${b.time}`);
        return dateA - dateB;
      })
      .slice(0, limit);
  },

  // Generate calendar event data
  generateCalendarEvent(meeting) {
    // Handle different date formats more robustly
    let startDate;
    
    try {
      // Try parsing the combined date and time
      startDate = new Date(`${meeting.date} ${meeting.time}`);
      
      // If invalid, try alternative parsing methods
      if (isNaN(startDate.getTime())) {
        // Try ISO format if date includes time
        startDate = new Date(meeting.date);
        if (isNaN(startDate.getTime())) {
          // Fallback to current date if parsing fails
          logger.warn('Invalid date format in meeting', { meetingId: meeting.id, date: meeting.date });
          startDate = new Date();
        }
      }
    } catch (error) {
      logger.error('Error parsing meeting date', { error: error.message, meetingId: meeting.id });
      startDate = new Date(); // Fallback to current date
    }
    
    const endDate = new Date(startDate.getTime() + (meeting.duration || 60) * 60000);
    
    const event = {
      title: `Meeting: ${meeting.customer}`,
      description: `Meeting with ${meeting.customer}${meeting.restaurant ? ` at ${meeting.restaurant}` : ''}${meeting.notes ? `\\n\\nNotes: ${meeting.notes}` : ''}`,
      location: meeting.restaurant || '',
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      attendees: [
        meeting.customerEmail,
        meeting.salesRepEmail
      ].filter(Boolean)
    };
    
    meeting.calendarEvent = event;
    return event;
  },

  // Export meeting to ICS format
  exportToICS(meeting) {
    const event = meeting.calendarEvent || this.generateCalendarEvent(meeting);
    const formatDate = (date) => {
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          logger.warn('Invalid date for ICS formatting', { date });
          return new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        }
        return dateObj.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      } catch (error) {
        logger.error('Error formatting date for ICS', { error: error.message, date });
        return new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      }
    };
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:RepConnect',
      'BEGIN:VEVENT',
      `UID:${meeting.id}@meetingsync.com`,
      `DTSTART:${formatDate(event.start)}`,
      `DTEND:${formatDate(event.end)}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description}`,
      `LOCATION:${event.location}`,
      'STATUS:CONFIRMED',
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Meeting reminder',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\\r\\n');
    
    return icsContent;
  },

  // Generate downloadable ICS file
  downloadICS(meeting, filename = null) {
    const icsContent = this.exportToICS(meeting);
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `meeting-${meeting.id}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Export all meetings for a user as ICS
  exportUserMeetingsICS(userId, userType) {
    const meetings = this.getMeetingsForUser(userId, userType);
    const upcomingMeetings = meetings.filter(m => {
      const meetingDate = new Date(`${m.date} ${m.time}`);
      return meetingDate >= new Date() && (m.status === 'confirmed' || m.status === 'pending');
    });

    if (upcomingMeetings.length === 0) {
      return null;
    }

    const formatDate = (date) => {
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          logger.warn('Invalid date for ICS formatting', { date });
          return new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        }
        return dateObj.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      } catch (error) {
        logger.error('Error formatting date for ICS', { error: error.message, date });
        return new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      }
    };
    
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:RepConnect',
      'METHOD:PUBLISH',
      'X-WR-CALNAME:RepConnect Meetings',
      'X-WR-CALDESC:Healthcare Meetings Schedule'
    ];

    upcomingMeetings.forEach(meeting => {
      const event = this.generateCalendarEvent(meeting);
      icsContent.push(
        'BEGIN:VEVENT',
        `UID:${meeting.id}@meetingsync.com`,
        `DTSTART:${formatDate(event.start)}`,
        `DTEND:${formatDate(event.end)}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${event.description}`,
        `LOCATION:${event.location}`,
        'STATUS:CONFIRMED',
        'BEGIN:VALARM',
        'TRIGGER:-PT15M',
        'ACTION:DISPLAY',
        'DESCRIPTION:Meeting reminder',
        'END:VALARM',
        'END:VEVENT'
      );
    });

    icsContent.push('END:VCALENDAR');
    
    const blob = new Blob([icsContent.join('\\r\\n')], { type: 'text/calendar' });
    const url = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `meetingsync-calendar-${userId}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return icsContent.join('\\r\\n');
  },

  // Generate calendar subscription URLs
  getCalendarSubscriptionUrls(userId, userType) {
    const baseUrl = window.location.origin;
    const token = `secure_token_${userId}`;
    
    return {
      webcal: `webcal://${window.location.host}/api/calendar/${userId}?type=${userType}&token=${token}`,
      ical: `${baseUrl}/api/calendar/${userId}.ics?type=${userType}&token=${token}`,
      google: `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(`webcal://${window.location.host}/api/calendar/${userId}?type=${userType}&token=${token}`)}`,
      outlook: `https://outlook.live.com/calendar/0/addcalendar?url=${encodeURIComponent(`${baseUrl}/api/calendar/${userId}.ics?type=${userType}&token=${token}`)}`,
      apple: `${baseUrl}/api/calendar/${userId}.ics?type=${userType}&token=${token}`,
      // For demonstration - data URL with calendar content
      dataUrl: this.generateDataUrlCalendar(userId, userType)
    };
  },

  // Generate a data URL with calendar content for immediate subscription
  generateDataUrlCalendar(userId, userType) {
    const meetings = this.getMeetingsForUser(userId, userType);
    const upcomingMeetings = meetings.filter(m => {
      const meetingDate = new Date(`${m.date} ${m.time}`);
      return meetingDate >= new Date() && (m.status === 'confirmed' || m.status === 'pending');
    });

    if (upcomingMeetings.length === 0) {
      return null;
    }

    const formatDate = (date) => {
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
          logger.warn('Invalid date for ICS formatting', { date });
          return new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        }
        return dateObj.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      } catch (error) {
        logger.error('Error formatting date for ICS', { error: error.message, date });
        return new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      }
    };
    
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:RepConnect',
      'METHOD:PUBLISH',
      'X-WR-CALNAME:RepConnect - Healthcare Meetings',
      'X-WR-CALDESC:Healthcare meeting schedule from RepConnect',
      'X-WR-TIMEZONE:America/Los_Angeles',
      'REFRESH-INTERVAL;VALUE=DURATION:PT1H'
    ];

    upcomingMeetings.forEach(meeting => {
      const event = this.generateCalendarEvent(meeting);
      icsContent.push(
        'BEGIN:VEVENT',
        `UID:${meeting.id}@meetingsync.com`,
        `DTSTART:${formatDate(event.start)}`,
        `DTEND:${formatDate(event.end)}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
        `LOCATION:${event.location}`,
        'STATUS:CONFIRMED',
        'TRANSP:OPAQUE',
        `CREATED:${formatDate(meeting.createdAt)}`,
        `LAST-MODIFIED:${formatDate(meeting.updatedAt || meeting.createdAt)}`,
        'BEGIN:VALARM',
        'TRIGGER:-PT15M',
        'ACTION:DISPLAY',
        'DESCRIPTION:Meeting reminder - 15 minutes',
        'END:VALARM',
        'BEGIN:VALARM',
        'TRIGGER:-PT1H',
        'ACTION:DISPLAY', 
        'DESCRIPTION:Meeting reminder - 1 hour',
        'END:VALARM',
        'END:VEVENT'
      );
    });

    icsContent.push('END:VCALENDAR');
    
    const icsString = icsContent.join('\\r\\n');
    return `data:text/calendar;charset=utf8,${encodeURIComponent(icsString)}`;
  },

  // Subscribe to calendar updates (simulated)
  subscribeToCalendarUpdates(userId, userType, options = {}) {
    const subscriptionKey = `calendar_subscription_${userId}`;
    const subscription = {
      userId,
      userType,
      subscribed: true,
      subscribedAt: new Date().toISOString(),
      options: {
        includeDetails: options.includeDetails !== false,
        includePending: options.includePending !== false,
        includeCompleted: options.includeCompleted || false,
        syncFrequency: options.syncFrequency || 'real-time',
        ...options
      }
    };
    
    localStorage.setItem(subscriptionKey, JSON.stringify(subscription));
    return subscription;
  },

  // Get calendar subscription status
  getCalendarSubscription(userId) {
    const subscriptionKey = `calendar_subscription_${userId}`;
    const subscription = localStorage.getItem(subscriptionKey);
    return subscription ? JSON.parse(subscription) : null;
  },

  // Update calendar subscription
  updateCalendarSubscription(userId, updates) {
    const current = this.getCalendarSubscription(userId);
    if (current) {
      const updated = {
        ...current,
        ...updates,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem(`calendar_subscription_${userId}`, JSON.stringify(updated));
      return updated;
    }
    return null;
  },

  // Sync meeting status between users
  syncMeetingStatus(meetingId, status, updatedBy) {
    const meeting = this.updateMeeting(meetingId, {
      status: status,
      lastUpdatedBy: updatedBy,
      sync: {
        needsSync: false,
        lastSynced: new Date().toISOString()
      }
    });
    
    return meeting;
  },

  // Get meeting statistics for a user
  getMeetingStats(userId, userType) {
    const meetings = this.getMeetingsForUser(userId, userType);
    const now = new Date();

    const upcoming = meetings.filter(m => {
      const meetingDate = new Date(`${m.date} ${m.time}`);
      return meetingDate >= now && (m.status === 'confirmed' || m.status === 'pending');
    }).length;

    const completed = meetings.filter(m => m.status === 'completed').length;
    const pending = meetings.filter(m => m.status === 'pending').length;
    const confirmed = meetings.filter(m => m.status === 'confirmed').length;
    const cancelled = meetings.filter(m => m.status === 'cancelled').length;
    const declined = meetings.filter(m => m.status === 'declined').length;

    const thisMonth = meetings.filter(m => {
      const meetingDate = new Date(m.date);
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return meetingDate >= thisMonthStart && meetingDate < nextMonthStart;
    }).length;

    // Calculate total spending (excluding cancelled meetings)
    const activeMeetings = meetings.filter(m => m.status !== 'cancelled' && m.status !== 'declined');
    const totalSpent = activeMeetings.reduce((sum, m) => {
      return sum + (parseFloat(m.totalCost || m.estimatedCost || 0));
    }, 0);

    // Count unique providers/customers interacted with (excluding cancelled)
    const uniqueProviders = new Set();
    activeMeetings.forEach(m => {
      if (userType === 'sales' && m.customerId) {
        uniqueProviders.add(m.customerId);
      } else if (userType === 'customer' && m.salesRepId) {
        uniqueProviders.add(m.salesRepId);
      }
    });

    return {
      total: meetings.length,
      upcoming,
      completed,
      pending,
      confirmed,
      cancelled,
      declined,
      thisMonth,
      totalSpent,
      uniqueProviders: uniqueProviders.size
    };
  },

  // Meeting Request specific methods
  
  // Get pending meeting requests for a healthcare provider
  getMeetingRequestsForProvider(providerId) {
    const meetings = this.getMeetingsForUser(providerId, 'customer');
    return meetings.filter(meeting => 
      meeting.status === 'pending' || 
      meeting.status === 'requested' || 
      meeting.status === 'needs-confirmation'
    );
  },

  // Get meeting requests by sales rep
  getMeetingRequestsBySalesRep(salesRepId) {
    const allMeetings = this.getAllMeetings();
    return allMeetings.filter(meeting => 
      meeting.salesRepId === salesRepId && (
        meeting.status === 'pending' || 
        meeting.status === 'requested' || 
        meeting.status === 'needs-confirmation'
      )
    );
  },

  // Approve a meeting request
  approveMeetingRequest(meetingId, approvalData = {}) {
    const approval = {
      status: 'confirmed',
      confirmedAt: new Date().toISOString(),
      confirmedBy: approvalData.confirmedBy,
      physicianNotes: approvalData.notes || '',
      finalStaffList: approvalData.staffList || [],
      specialRequests: approvalData.specialRequests || '',
      ...approvalData
    };

    const updatedMeeting = this.updateMeeting(meetingId, approval);

    if (updatedMeeting) {
      // Add to sync queue for Supabase
      try {
        const { offlineSyncManager } = require('./supabase');
        offlineSyncManager.addToSyncQueue({
          type: 'update_meeting',
          data: approval,
          meetingId: meetingId
        });
      } catch (error) {
        logger.debug('📡 Sync manager not available, operation stored locally only');
      }

      // Create notification for sales rep
      this.createNotification({
        type: 'meeting_approved',
        recipientId: updatedMeeting.salesRepId,
        recipientType: 'sales',
        meetingId: meetingId,
        message: `Your meeting request with ${updatedMeeting.customer} has been approved`,
        data: {
          customerName: updatedMeeting.customer,
          meetingDate: updatedMeeting.date,
          meetingTime: updatedMeeting.time
        }
      });
    }

    return updatedMeeting;
  },

  // Decline a meeting request
  declineMeetingRequest(meetingId, declineData = {}) {
    const decline = {
      status: 'declined',
      declinedAt: new Date().toISOString(),
      declinedBy: declineData.declinedBy,
      declineReason: declineData.reason || '',
      ...declineData
    };

    const updatedMeeting = this.updateMeeting(meetingId, decline);

    if (updatedMeeting) {
      // Add to sync queue for Supabase
      try {
        const { offlineSyncManager } = require('./supabase');
        offlineSyncManager.addToSyncQueue({
          type: 'update_meeting',
          data: decline,
          meetingId: meetingId
        });
      } catch (error) {
        logger.debug('📡 Sync manager not available, operation stored locally only');
      }

      // Create notification for sales rep
      this.createNotification({
        type: 'meeting_declined',
        recipientId: updatedMeeting.salesRepId,
        recipientType: 'sales',
        meetingId: meetingId,
        message: `Your meeting request with ${updatedMeeting.customer} has been declined`,
        data: {
          customerName: updatedMeeting.customer,
          meetingDate: updatedMeeting.date,
          meetingTime: updatedMeeting.time,
          reason: decline.declineReason
        }
      });
    }

    return updatedMeeting;
  },

  // Cancel a meeting (by either sales rep or physician)
  cancelMeeting(meetingId, cancellationData = {}) {
    const meeting = this.getMeetingById(meetingId);
    if (!meeting) {
      throw new Error('Meeting not found');
    }

    // Only allow cancellation of confirmed, pending, or approved meetings
    const allowedStatuses = ['confirmed', 'pending', 'approved'];
    if (!allowedStatuses.includes(meeting.status)) {
      throw new Error(`Cannot cancel meeting with status: ${meeting.status}`);
    }

    const cancellation = {
      status: 'cancelled',
      cancelledAt: new Date().toISOString(),
      cancelledBy: cancellationData.cancelledBy,
      cancelledByType: cancellationData.cancelledByType, // 'sales' or 'customer'
      cancellationReason: cancellationData.reason || '',
      cancellationCategory: cancellationData.category || 'other', // 'scheduling_conflict', 'emergency', 'illness', 'other'
      allowReschedule: cancellationData.allowReschedule !== false, // Default to allowing reschedule
      originalStatus: meeting.status, // Track what status it was before cancellation
      ...cancellationData
    };

    const updatedMeeting = this.updateMeeting(meetingId, cancellation);

    if (updatedMeeting) {
      // Determine who to notify based on who cancelled
      const isCustomerCancelling = cancellationData.cancelledByType === 'customer';
      const recipientId = isCustomerCancelling ? updatedMeeting.salesRepId : updatedMeeting.customerId;
      const recipientType = isCustomerCancelling ? 'sales' : 'customer';
      const cancellerName = isCustomerCancelling ? updatedMeeting.customer : updatedMeeting.salesRepName;
      const notificationType = isCustomerCancelling ? 'meeting_cancelled_by_customer' : 'meeting_cancelled_by_sales';

      // Create notification for the other party
      this.createNotification({
        type: notificationType,
        recipientId: recipientId,
        recipientType: recipientType,
        meetingId: meetingId,
        urgent: this.isCancellationUrgent(updatedMeeting),
        message: `Meeting with ${cancellerName} has been cancelled`,
        data: {
          cancellerName: cancellerName,
          cancellerType: cancellationData.cancelledByType,
          meetingDate: updatedMeeting.date,
          meetingTime: updatedMeeting.time,
          reason: cancellation.cancellationReason,
          category: cancellation.cancellationCategory,
          allowReschedule: cancellation.allowReschedule,
          restaurant: updatedMeeting.restaurant
        }
      });

      // If rescheduling is suggested, create additional notification
      if (cancellationData.suggestReschedule && cancellationData.suggestedDates) {
        this.createNotification({
          type: 'reschedule_suggestion',
          recipientId: recipientId,
          recipientType: recipientType,
          meetingId: meetingId,
          message: `${cancellerName} suggested new dates for your cancelled meeting`,
          data: {
            originalMeetingId: meetingId,
            suggestedDates: cancellationData.suggestedDates,
            cancellerName: cancellerName
          }
        });
      }
    }

    return updatedMeeting;
  },

  // Check if a cancellation is urgent (meeting is within 24 hours)
  isCancellationUrgent(meeting) {
    try {
      const meetingDate = new Date(`${meeting.date} ${meeting.time}`);
      const now = new Date();
      const hoursUntilMeeting = (meetingDate - now) / (1000 * 60 * 60);
      return hoursUntilMeeting <= 24 && hoursUntilMeeting > 0; // Urgent if within 24 hours but not past
    } catch (error) {
      logger.error('Error calculating cancellation urgency', { error: error.message });
      return false;
    }
  },

  // Get cancellation statistics for analytics
  getCancellationStats(userId, userType, timeframe = 30) {
    const meetings = this.getMeetingsForUser(userId, userType);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - timeframe);

    const recentMeetings = meetings.filter(m => {
      const meetingDate = new Date(m.createdAt || m.date);
      return meetingDate >= cutoffDate;
    });

    const cancelled = recentMeetings.filter(m => m.status === 'cancelled');
    const cancelledByUser = cancelled.filter(m => m.cancelledByType === userType);
    const cancelledByOther = cancelled.filter(m => m.cancelledByType !== userType);

    // Group by cancellation categories
    const categoryCounts = cancelled.reduce((acc, meeting) => {
      const category = meeting.cancellationCategory || 'other';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    return {
      totalMeetings: recentMeetings.length,
      totalCancelled: cancelled.length,
      cancelledByUser: cancelledByUser.length,
      cancelledByOther: cancelledByOther.length,
      cancellationRate: recentMeetings.length > 0 ? (cancelled.length / recentMeetings.length) * 100 : 0,
      categories: categoryCounts,
      urgentCancellations: cancelled.filter(m => this.isCancellationUrgent(m)).length
    };
  },

  // Request reschedule after cancellation
  requestReschedule(originalMeetingId, rescheduleData = {}) {
    const originalMeeting = this.getMeetingById(originalMeetingId);
    if (!originalMeeting || originalMeeting.status !== 'cancelled') {
      throw new Error('Original meeting not found or not cancelled');
    }

    // Create new meeting request based on the cancelled meeting
    const rescheduleRequest = {
      ...originalMeeting,
      id: undefined, // Will get new ID when saved
      status: 'pending',
      originalMeetingId: originalMeetingId,
      isReschedule: true,
      rescheduleRequestedAt: new Date().toISOString(),
      rescheduleRequestedBy: rescheduleData.requestedBy,
      rescheduleRequestedByType: rescheduleData.requestedByType,
      date: rescheduleData.newDate || originalMeeting.date,
      time: rescheduleData.newTime || originalMeeting.time,
      rescheduleReason: rescheduleData.reason || 'Rescheduling after cancellation',
      ...rescheduleData
    };

    // Remove fields that shouldn't be copied
    delete rescheduleRequest.cancelledAt;
    delete rescheduleRequest.cancelledBy;
    delete rescheduleRequest.cancellationReason;
    delete rescheduleRequest.originalStatus;

    const newMeeting = this.saveMeeting(rescheduleRequest);

    if (newMeeting) {
      // Determine who to notify
      const isCustomerRequesting = rescheduleData.requestedByType === 'customer';
      const recipientId = isCustomerRequesting ? originalMeeting.salesRepId : originalMeeting.customerId;
      const recipientType = isCustomerRequesting ? 'sales' : 'customer';
      const requesterName = isCustomerRequesting ? originalMeeting.customer : originalMeeting.salesRepName;

      // Create notification for reschedule request
      this.createNotification({
        type: 'reschedule_request',
        recipientId: recipientId,
        recipientType: recipientType,
        meetingId: newMeeting.id,
        message: `${requesterName} requested to reschedule your cancelled meeting`,
        data: {
          originalMeetingId: originalMeetingId,
          newMeetingId: newMeeting.id,
          requesterName: requesterName,
          newDate: rescheduleRequest.date,
          newTime: rescheduleRequest.time,
          reason: rescheduleRequest.rescheduleReason
        }
      });
    }

    return newMeeting;
  },

  // Create notification
  createNotification(notificationData) {
    try {
      const notifications = this.getAllNotifications();
      const newNotification = {
        ...notificationData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        read: false,
        urgent: notificationData.urgent || false
      };

      notifications.push(newNotification);
      localStorage.setItem('meetingsync_notifications', JSON.stringify(notifications));

      // Add to sync queue for Supabase
      try {
        const { offlineSyncManager } = require('./supabase');
        offlineSyncManager.addToSyncQueue({
          type: 'create_notification',
          data: newNotification,
          localId: newNotification.id
        });
      } catch (error) {
        logger.debug('📡 Sync manager not available, notification stored locally only');
      }

      return newNotification;
    } catch (error) {
      logger.error('Error creating notification', { error: error.message });
      return null;
    }
  },

  // Get all notifications
  getAllNotifications() {
    try {
      const notifications = localStorage.getItem('meetingsync_notifications');
      return notifications ? JSON.parse(notifications) : [];
    } catch (error) {
      logger.error('Error getting notifications', { error: error.message });
      return [];
    }
  },

  // Get notifications for a specific user
  getNotificationsForUser(userId, userType) {
    const notifications = this.getAllNotifications();
    return notifications.filter(notification => 
      notification.recipientId === userId && notification.recipientType === userType
    ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  },

  // Mark notification as read
  markNotificationAsRead(notificationId) {
    try {
      const notifications = this.getAllNotifications();
      const notificationIndex = notifications.findIndex(n => n.id === notificationId);

      if (notificationIndex >= 0) {
        notifications[notificationIndex].read = true;
        notifications[notificationIndex].readAt = new Date().toISOString();
        localStorage.setItem('meetingsync_notifications', JSON.stringify(notifications));

        // Add to sync queue for Supabase
        try {
          const { offlineSyncManager } = require('./supabase');
          offlineSyncManager.addToSyncQueue({
            type: 'update_notification',
            data: notifications[notificationIndex],
            localId: notificationId
          });
        } catch (error) {
          logger.debug('📡 Sync manager not available, notification update stored locally only');
        }

        return true;
      }
      return false;
    } catch (error) {
      logger.error('Error marking notification as read', { error: error.message });
      return false;
    }
  },

  // Get unread notification count for a user
  getUnreadNotificationCount(userId, userType) {
    const notifications = this.getNotificationsForUser(userId, userType);
    return notifications.filter(n => !n.read).length;
  },

  // Auto-generate meeting requests from BookMeetingView data
  createMeetingRequest(requestData) {
    const meetingData = {
      ...requestData,
      status: 'pending',
      requestedAt: new Date().toISOString(),
      requestType: 'lunch_meeting',
      needsApproval: true
    };

    const newMeeting = this.saveMeeting(meetingData);

    if (newMeeting) {
      // Add to sync queue for Supabase
      try {
        const { offlineSyncManager } = require('./supabase');
        offlineSyncManager.addToSyncQueue({
          type: 'create_meeting',
          data: meetingData,
          localId: newMeeting.id
        });
      } catch (error) {
        logger.debug('📡 Sync manager not available, operation stored locally only');
      }

      // Create notification for healthcare provider
      this.createNotification({
        type: 'meeting_request',
        recipientId: requestData.customerId,
        recipientType: 'customer',
        meetingId: newMeeting.id,
        urgent: this.isUrgentRequest(requestData),
        message: `New meeting request from ${requestData.salesRepName || 'sales representative'}`,
        data: {
          salesRepName: requestData.salesRepName,
          meetingDate: requestData.date,
          meetingTime: requestData.time,
          restaurant: requestData.restaurant,
          estimatedCost: requestData.totalCost
        }
      });
    }

    return newMeeting;
  },

  // Determine if a request is urgent (meeting is within 2 days)
  isUrgentRequest(requestData) {
    const meetingDate = new Date(requestData.date);
    const now = new Date();
    const hoursUntilMeeting = (meetingDate - now) / (1000 * 60 * 60);
    return hoursUntilMeeting <= 48; // Urgent if within 48 hours
  },

  // Bulk approve/decline requests
  bulkUpdateRequestStatus(requestIds, status, updateData = {}) {
    const results = [];
    
    for (const requestId of requestIds) {
      let result;
      if (status === 'confirmed') {
        result = this.approveMeetingRequest(requestId, updateData);
      } else if (status === 'declined') {
        result = this.declineMeetingRequest(requestId, updateData);
      }
      
      if (result) {
        results.push(result);
      }
    }
    
    return results;
  },

  // Get request statistics for a provider
  getRequestStatsForProvider(providerId) {
    const requests = this.getMeetingRequestsForProvider(providerId);
    const now = new Date();
    
    const urgent = requests.filter(r => {
      const meetingDate = new Date(r.date);
      const hoursUntilMeeting = (meetingDate - now) / (1000 * 60 * 60);
      return hoursUntilMeeting <= 24;
    }).length;

    const withStaff = requests.filter(r => r.hasStaff || r.staffCount > 0).length;
    
    const today = requests.filter(r => {
      const requestDate = new Date(r.createdAt || r.requestedAt);
      return requestDate.toDateString() === now.toDateString();
    }).length;

    return {
      total: requests.length,
      urgent,
      withStaff,
      today,
      pending: requests.filter(r => r.status === 'pending').length
    };
  },

  // Meeting-derived preference tracking methods

  /**
   * Mark meeting as completed and extract preferences
   * @param {string} meetingId - Meeting ID
   * @param {Object} feedbackData - Meeting feedback and outcomes
   * @param {string} updaterId - User marking as complete
   * @returns {Object} Updated meeting with derived enhancements
   */
  completeMeeting(meetingId, feedbackData = {}, updaterId) {
    try {
      // Update meeting status
      const updatedMeeting = this.updateMeeting(meetingId, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        completedBy: updaterId,
        feedback: feedbackData
      });

      if (updatedMeeting) {
        // Extract and save derived preferences
        this.extractMeetingPreferences(updatedMeeting, updaterId);
      }

      return updatedMeeting;
    } catch (error) {
      logger.error('Error completing meeting', { error: error.message });
      return null;
    }
  },

  /**
   * Extract preferences from completed meeting and create enhancements
   * @param {Object} meeting - Completed meeting data
   * @param {string} contributorId - User who completed the meeting
   */
  extractMeetingPreferences(meeting, contributorId) {
    try {
      const preferences = this.analyzeMeetingForPreferences(meeting);
      
      // Save each derived preference as an enhancement
      preferences.forEach(preference => {
        ProfileEnhancementStorage.addMeetingDerivedEnhancements(
          meeting.customerId,
          {
            id: meeting.id,
            [preference.field]: preference.value,
            confidence: preference.confidence,
            source: preference.source
          },
          contributorId
        );
      });

      logger.debug(`Extracted ${preferences.length} preferences from meeting ${meeting.id}`);
    } catch (error) {
      logger.error('Error extracting meeting preferences', { error: error.message });
    }
  },

  /**
   * Analyze meeting data to identify preferences
   * @param {Object} meeting - Meeting data
   * @returns {Array} Array of preference objects
   */
  analyzeMeetingForPreferences(meeting) {
    const preferences = [];

    // Restaurant preference from successful meetings
    if (meeting.restaurant && meeting.feedback) {
      const restaurantRating = meeting.feedback.restaurantRating || 0;
      const overallRating = meeting.feedback.overallRating || 0;
      
      if (restaurantRating >= 4 || overallRating >= 4) {
        preferences.push({
          field: 'restaurantPreference',
          value: meeting.restaurant,
          confidence: Math.max(restaurantRating, overallRating) / 5,
          source: 'meeting_feedback'
        });
      }
    }

    // Meeting time preference from booking patterns
    if (meeting.time) {
      const hour = parseInt(meeting.time.split(':')[0]);
      let timePreference = '';
      let confidence = 0.7;

      if (hour < 12) {
        timePreference = 'Early lunch (11:00-12:00)';
      } else if (hour === 12) {
        timePreference = 'Standard lunch (12:00-1:00)';
      } else {
        timePreference = 'Late lunch (1:00-2:00)';
      }

      // Higher confidence if this matches their stated preference
      if (meeting.feedback?.timePreferenceConfirmed) {
        confidence = 0.9;
      }

      preferences.push({
        field: 'timePreference',
        value: timePreference,
        confidence: confidence,
        source: 'meeting_scheduling'
      });
    }

    // Dietary restrictions from order analysis
    if (meeting.orderItems && meeting.orderItems.length > 0) {
      const dietaryPatterns = this.analyzeDietaryPatterns(meeting.orderItems);
      dietaryPatterns.forEach(pattern => {
        preferences.push({
          field: 'dietaryRestrictions',
          value: pattern.restriction,
          confidence: pattern.confidence,
          source: 'order_analysis'
        });
      });
    }

    // Communication style from meeting notes
    if (meeting.notes || meeting.feedback?.communicationNotes) {
      const communicationStyle = this.analyzeCommunicationStyle(
        meeting.notes, 
        meeting.feedback?.communicationNotes
      );
      
      if (communicationStyle) {
        preferences.push({
          field: 'communicationStyle',
          value: communicationStyle.style,
          confidence: communicationStyle.confidence,
          source: 'meeting_observation'
        });
      }
    }

    // Meeting duration preference
    if (meeting.actualDuration && meeting.actualDuration !== meeting.duration) {
      const durationPreference = this.analyzeDurationPreference(
        meeting.duration, 
        meeting.actualDuration
      );
      
      if (durationPreference) {
        preferences.push({
          field: 'preferredMeetingDuration',
          value: durationPreference.duration,
          confidence: durationPreference.confidence,
          source: 'meeting_timing'
        });
      }
    }

    return preferences.filter(p => p.confidence >= 0.5); // Only high-confidence preferences
  },

  /**
   * Analyze order items for dietary patterns
   * @param {Array} orderItems - Items ordered
   * @returns {Array} Dietary restriction patterns
   */
  analyzeDietaryPatterns(orderItems) {
    const patterns = [];
    
    // Check for vegetarian patterns
    const hasVegetarian = orderItems.every(item => 
      !item.name.toLowerCase().includes('chicken') &&
      !item.name.toLowerCase().includes('beef') &&
      !item.name.toLowerCase().includes('pork') &&
      !item.name.toLowerCase().includes('fish')
    );
    
    if (hasVegetarian && orderItems.length > 1) {
      patterns.push({
        restriction: 'Vegetarian',
        confidence: 0.8
      });
    }

    // Check for gluten-free patterns
    const hasGlutenFree = orderItems.some(item =>
      item.name.toLowerCase().includes('gluten-free') ||
      item.name.toLowerCase().includes('salad')
    );
    
    if (hasGlutenFree) {
      patterns.push({
        restriction: 'Gluten-free',
        confidence: 0.6
      });
    }

    return patterns;
  },

  /**
   * Analyze communication style from meeting notes
   * @param {string} notes - Meeting notes
   * @param {string} communicationNotes - Additional communication feedback
   * @returns {Object} Communication style analysis
   */
  analyzeCommunicationStyle(notes = '', communicationNotes = '') {
    const combinedNotes = `${notes} ${communicationNotes}`.toLowerCase();
    
    const patterns = {
      'Direct and brief': ['quick', 'brief', 'straight to point', 'efficient', 'short'],
      'Detailed explanations': ['detailed', 'thorough', 'comprehensive', 'in-depth', 'explained'],
      'Relationship-focused': ['personal', 'family', 'relationship', 'friendly', 'casual'],
      'Data-driven': ['numbers', 'data', 'research', 'studies', 'evidence', 'statistics']
    };

    let bestMatch = null;
    let highestScore = 0;

    Object.entries(patterns).forEach(([style, keywords]) => {
      const score = keywords.reduce((count, keyword) => {
        return count + (combinedNotes.includes(keyword) ? 1 : 0);
      }, 0);

      if (score > highestScore) {
        highestScore = score;
        bestMatch = style;
      }
    });

    if (bestMatch && highestScore >= 2) {
      return {
        style: bestMatch,
        confidence: Math.min(0.9, 0.5 + (highestScore * 0.1))
      };
    }

    return null;
  },

  /**
   * Analyze duration preference
   * @param {number} planned - Planned duration in minutes
   * @param {number} actual - Actual duration in minutes
   * @returns {Object} Duration preference analysis
   */
  analyzeDurationPreference(planned, actual) {
    const difference = actual - planned;
    const percentDiff = Math.abs(difference) / planned;

    if (percentDiff > 0.2) { // Significant difference
      if (difference > 0) {
        return {
          duration: 'Extended meetings (90+ minutes)',
          confidence: Math.min(0.8, 0.5 + percentDiff)
        };
      } else {
        return {
          duration: 'Brief meetings (45-60 minutes)',
          confidence: Math.min(0.8, 0.5 + percentDiff)
        };
      }
    }

    return null;
  },

  /**
   * Get all derived preferences for a provider
   * @param {string} providerId - Provider ID
   * @param {string} userId - User requesting data
   * @returns {Object} Summary of derived preferences
   */
  getDerivedPreferencesForProvider(providerId, userId) {
    try {
      const meetings = this.getAllMeetings().filter(m => 
        m.customerId === providerId && 
        m.status === 'completed' &&
        m.salesRepId === userId
      );

      const summary = {
        totalMeetings: meetings.length,
        preferences: {
          restaurants: {},
          timeSlots: {},
          communicationStyles: {},
          dietaryPatterns: {}
        },
        lastAnalyzed: new Date().toISOString()
      };

      meetings.forEach(meeting => {
        // Count restaurant preferences
        if (meeting.restaurant && meeting.feedback?.restaurantRating >= 4) {
          summary.preferences.restaurants[meeting.restaurant] = 
            (summary.preferences.restaurants[meeting.restaurant] || 0) + 1;
        }

        // Count time preferences
        if (meeting.time) {
          const hour = parseInt(meeting.time.split(':')[0]);
          const timeSlot = hour < 12 ? 'early' : hour === 12 ? 'standard' : 'late';
          summary.preferences.timeSlots[timeSlot] = 
            (summary.preferences.timeSlots[timeSlot] || 0) + 1;
        }
      });

      return summary;
    } catch (error) {
      logger.error('Error getting derived preferences', { error: error.message });
      return null;
    }
  },

  // Check if a time slot is available for a provider
  isTimeSlotAvailable(providerId, date, time, excludeMeetingId = null) {
    try {
      const meetings = this.getAllMeetings();

      // Find meetings for this provider on this date/time
      const conflictingMeetings = meetings.filter(meeting => {
        // Skip the meeting we're editing (for reschedule scenarios)
        if (excludeMeetingId && meeting.id === excludeMeetingId) {
          return false;
        }

        // Check if this meeting involves the provider
        const isProviderInvolved = meeting.customerId === providerId ||
                                 meeting.salesRepId === providerId;

        if (!isProviderInvolved) return false;

        // Skip cancelled or declined meetings - these free up the slot
        if (meeting.status === 'cancelled' || meeting.status === 'declined') {
          return false;
        }

        // Check for date/time match
        return meeting.date === date && meeting.time === time;
      });

      return conflictingMeetings.length === 0;
    } catch (error) {
      logger.error('Error checking time slot availability', { error: error.message });
      return true; // Default to available if there's an error
    }
  },

  // Get available time slots for a provider on a specific date
  getAvailableTimeSlots(providerId, date, excludeMeetingId = null) {
    const commonTimeSlots = [
      '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
      '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
      '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM'
    ];

    return commonTimeSlots.filter(timeSlot =>
      this.isTimeSlotAvailable(providerId, date, timeSlot, excludeMeetingId)
    );
  },

  // Get provider's scheduled meetings for a date (excluding cancelled)
  getProviderMeetingsForDate(providerId, date) {
    try {
      const meetings = this.getAllMeetings();

      return meetings.filter(meeting => {
        const isProviderInvolved = meeting.customerId === providerId ||
                                 meeting.salesRepId === providerId;

        const isActiveStatus = meeting.status !== 'cancelled' &&
                               meeting.status !== 'declined';

        return isProviderInvolved && meeting.date === date && isActiveStatus;
      }).sort((a, b) => {
        // Sort by time
        const timeA = this.timeToMinutes(a.time);
        const timeB = this.timeToMinutes(b.time);
        return timeA - timeB;
      });
    } catch (error) {
      logger.error('Error getting provider meetings for date', { error: error.message });
      return [];
    }
  },

  // Helper function to convert time string to minutes for sorting
  timeToMinutes(timeStr) {
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let totalMinutes = hours * 60 + (minutes || 0);

    if (period === 'PM' && hours !== 12) {
      totalMinutes += 12 * 60;
    } else if (period === 'AM' && hours === 12) {
      totalMinutes -= 12 * 60;
    }

    return totalMinutes;
  }
};